import {embedRequested,getEmbedLease,embedHeaders} from '@/features/saiEmbed/embedAccess';

export type AuthRuntimeTokenStatus =
  | "valid"
  | "unauthorized"
  | "transient"
  | "unavailable";

export interface AuthRuntimeProvider {
  getAccessToken: () => string | null;
  getAuthHeaders: () => HeadersInit;
  getSessionIdentity?: () => string | null;
  ensureValidToken?: () => Promise<boolean>;
  ensureValidTokenStatus?: () => Promise<AuthRuntimeTokenStatus>;
  /**
   * Called after the server rejected a request's bearer (for example when an
   * admin change expired the caller's own authority). Resolves true when a
   * newer access token is available for exactly one retry.
   */
  renewAfterUnauthorized?: (rejectedAuthorization: string | null) => Promise<boolean>;
}

let authRuntimeProvider: AuthRuntimeProvider | null = null;

export function registerAuthRuntimeProvider(
  provider: AuthRuntimeProvider | null
): void {
  authRuntimeProvider = provider;
}

export function hasAuthRuntimeProvider(): boolean {
  return authRuntimeProvider !== null;
}

export function getAuthRuntimeAccessToken(): string | null {
  if(embedRequested())return getEmbedLease()?.token??null;
  return authRuntimeProvider?.getAccessToken() ?? null;
}

export function getAuthRuntimeHeaders(): HeadersInit {
  if(embedRequested())return embedHeaders();
  return authRuntimeProvider?.getAuthHeaders() ?? {};
}

export function getAuthRuntimeSessionIdentity(): string | null {
  if(embedRequested()){
    const lease=getEmbedLease();return lease?`sai:${lease.workspace}:${lease.principalId}:${authRuntimeProvider?.getSessionIdentity?.()??''}`:null;
  }
  return authRuntimeProvider?.getSessionIdentity?.() ?? null;
}

export async function renewAuthRuntimeAfterUnauthorized(rejectedAuthorization: string | null): Promise<boolean> {
  if(embedRequested())return Boolean(getEmbedLease()&&`Bearer ${getEmbedLease()?.token}`!==rejectedAuthorization);
  if (!authRuntimeProvider?.renewAfterUnauthorized) {
    return false;
  }
  try {
    return (await authRuntimeProvider.renewAfterUnauthorized(rejectedAuthorization)) === true;
  } catch {
    return false;
  }
}

export async function ensureAuthRuntimeTokenStatus(): Promise<AuthRuntimeTokenStatus> {
  if(embedRequested())return getEmbedLease()?'valid':'unauthorized';
  if (!authRuntimeProvider) {
    return "unavailable";
  }
  if (authRuntimeProvider.ensureValidTokenStatus) {
    return authRuntimeProvider.ensureValidTokenStatus();
  }
  if (!authRuntimeProvider.ensureValidToken) {
    return "unavailable";
  }
  try {
    return (await authRuntimeProvider.ensureValidToken()) ? "valid" : "unauthorized";
  } catch {
    return "transient";
  }
}
